import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import Drawer from 'shared/components/drawer'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { useToggle } from 'shared/hooks/use-toggle'
import CapIcon from 'shared/icons/cap-icon'
import CloseIcon from 'shared/icons/close-icon'
import { CourseSideMenu } from '../course-side-menu'

interface MobileCourseSideMenuProps {
  className?: string
}

export default function MobileCourseSideMenu({ className }: MobileCourseSideMenuProps): ReactNode {
  const { t } = useTranslation()
  const [isOpenModuleList, toggle, setIsOpenModuleList] = useToggle()
  const buttonRef = useRef(null)
  const drawerRef = useRef(null)

  const Icon = isOpenModuleList ? CloseIcon : CapIcon
  const label = isOpenModuleList ? 'course.hide_curriculum' : 'course.show_curriculum'

  const handleClickOutside = useCallback(() => setIsOpenModuleList(false), [setIsOpenModuleList])
  useClickOutside(handleClickOutside, null, [buttonRef.current, drawerRef.current])

  useEffect(() => {
    if (!isOpenModuleList) return

    const handleBlur = (): void => {
      setTimeout(() => {
        if (document.activeElement?.tagName === 'IFRAME') {
          setIsOpenModuleList(false)
        }
      }, 0)
    }

    window.addEventListener('blur', handleBlur)
    return () => globalThis.removeEventListener('blur', handleBlur)
  }, [isOpenModuleList, setIsOpenModuleList])

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-testid="mobile-side-menu-toggle"
        onClick={toggle}
        className={twMerge(
          `flex size-16 items-center justify-center rounded-full bg-THEME-block-background p-3 shadow-mobile-menu`,
          className,
          isOpenModuleList && 'z-20',
        )}
        aria-label={t(label)}
      >
        <Icon
          className={twJoin(
            'pointer-events-none',
            isOpenModuleList ? 'size-6 text-THEME-primary' : 'text-THEME-secondary',
          )}
        />
      </button>
      <Drawer
        ref={drawerRef}
        controlled
        open={isOpenModuleList}
        className="bg-THEME-block-background"
      >
        <CourseSideMenu menuClassName="px-0" />
      </Drawer>
    </>
  )
}
